
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../dashboard/components/Sidebar';
import DashboardHeader from '../../dashboard/components/DashboardHeader';

const ProjectsOverview = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const categories = [
    {
      id: 'private-clients',
      name: 'Private Clients',
      description: 'Residential and personal construction projects',
      icon: 'ri-home-4-line',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      projectCount: 12,
      activeProjects: 8,
      totalValue: '$2.4M',
      image: 'https://public.readdy.ai/ai/img_res/0045bb6608559454b0225d71454bda63.jpg'
    },
    {
      id: 'securitas',
      name: 'Securitas',
      description: 'Security infrastructure and facility projects',
      icon: 'ri-shield-check-line',
      color: 'bg-red-500',
      hoverColor: 'hover:bg-red-600',
      projectCount: 8,
      activeProjects: 5,
      totalValue: '$1.8M',
      image: 'https://readdy.ai/api/search-image?query=modern%20security%20building%20with%20surveillance%20systems%20and%20professional%20security%20infrastructure%2C%20clean%20white%20background%2C%20corporate%20style%20photography&width=400&height=300&seq=securitas&orientation=landscape'
    },
    {
      id: 'cic',
      name: 'CIC',
      description: 'Commercial and industrial construction projects',
      icon: 'ri-building-line',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      projectCount: 15,
      activeProjects: 10,
      totalValue: '$5.2M',
      image: 'https://readdy.ai/api/search-image?query=modern%20commercial%20industrial%20building%20construction%20site%20with%20cranes%20and%20steel%20framework%2C%20clean%20white%20background%2C%20professional%20construction%20photography&width=400&height=300&seq=cic&orientation=landscape'
    },
    {
      id: 'johnson-controls',
      name: 'Johnson Controls',
      description: 'HVAC and building automation systems',
      icon: 'ri-settings-3-line',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      projectCount: 6,
      activeProjects: 4,
      totalValue: '$980K',
      image: 'https://readdy.ai/api/search-image?query=modern%20HVAC%20building%20automation%20control%20systems%20with%20digital%20panels%20and%20climate%20control%20equipment%2C%20clean%20white%20background%2C%20technical%20photography%20style&width=400&height=300&seq=johnson-controls&orientation=landscape'
    },
    {
      id: 'others',
      name: 'Others',
      description: 'Miscellaneous and specialty projects',
      icon: 'ri-more-line',
      color: 'bg-gray-500',
      hoverColor: 'hover:bg-gray-600',
      projectCount: 4,
      activeProjects: 2,
      totalValue: '$650K',
      image: 'https://readdy.ai/api/search-image?query=diverse%20construction%20and%20renovation%20projects%20including%20specialty%20work%20and%20unique%20building%20solutions%2C%20clean%20white%20background%2C%20professional%20photography&width=400&height=300&seq=others&orientation=landscape'
    }
  ];

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/projects/${categoryId}`);
  };

  const totalProjects = categories.reduce((sum, cat) => sum + cat.projectCount, 0);
  const totalActiveProjects = categories.reduce((sum, cat) => sum + cat.activeProjects, 0);
  const totalValue = categories.reduce((sum, cat) => {
    const value = parseFloat(cat.totalValue.replace(/[$MK,]/g, ''));
    const multiplier = cat.totalValue.includes('M') ? 1000000 : cat.totalValue.includes('K') ? 1000 : 1;
    return sum + (value * multiplier);
  }, 0);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <DashboardHeader onMenuClick={() => setSidebarOpen(true)} />
        
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Projects Overview</h1>
              <p className="text-gray-600">Select a project category to view and manage specific projects</p>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-folder-line text-blue-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Projects</p>
                    <p className="text-2xl font-bold text-gray-900">{totalProjects}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-play-circle-line text-green-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Active Projects</p>
                    <p className="text-2xl font-bold text-gray-900">{totalActiveProjects}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-purple-600 text-xl"></i>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Value</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalValue)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Project Categories */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <div
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer transition-all hover:shadow-md hover:border-gray-300 group"
                >
                  {/* Category Image */}
                  <div className="h-48 bg-gray-100 overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  {/* Category Content */}
                  <div className="p-6">
                    <div className="flex items-center mb-3">
                      <div className={`w-10 h-10 ${category.color} rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform`}>
                        <i className={`${category.icon} text-white text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {category.name}
                        </h3>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {category.description}
                    </p>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-900">{category.projectCount}</p>
                        <p className="text-xs text-gray-500">Total</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600">{category.activeProjects}</p>
                        <p className="text-xs text-gray-500">Active</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-purple-600">{category.totalValue}</p>
                        <p className="text-xs text-gray-500">Value</p>
                      </div>
                    </div>

                    {/* Action Arrow */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <span className="text-sm text-gray-500">View Projects</span>
                      <i className="ri-arrow-right-line text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap">
                  <i className="ri-add-line mr-2"></i>
                  New Project
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap">
                  <i className="ri-file-excel-line mr-2"></i>
                  Export Report
                </button>
                <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium whitespace-nowrap">
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Review
                </button>
                <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium whitespace-nowrap">
                  <i className="ri-settings-line mr-2"></i>
                  Project Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsOverview;
